import { useEffect, useId, useMemo, useRef } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { PaperclipIcon, SendHorizontalIcon, XIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from '@rocco/components/form';
import { Textarea } from '@rocco/components/textarea';
import {
    validateChatUploadFile,
    validateChatUploadFileCount,
} from '../../utils/validation';

const formSchema = z.object({
    message: z
        .string()
        .trim()
        .max(2000, { message: 'Must be less than 2000 characters' }),
    attachments: z.array(z.instanceof(File)),
});

/**
 * The text input for the chat box.
 * - The chat box text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 * @see {@link ChatBox}
 */
export function ChatBoxInput({
    className,
    isOpen,
    isDisabled = false,
    isSubmitting = false,
    onSend,
}: {
    className?: string;
    /**
     * Whether the chat box is open.
     */
    isOpen: boolean;
    /**
     * Whether the send button is disabled. Defaults to `false`.
     */
    isDisabled?: boolean;
    /**
     * Whether the form is submitting. Only used when sending a message with
     * file attachments. Defaults to `false`.
     */
    isSubmitting?: boolean;
    /**
     * Callback when the user sends a message.
     */
    onSend?: ({
        message,
        attachments,
    }: {
        message: string;
        attachments: File[];
    }) => void;
}) {
    const formId = useId();
    const formRef = useRef<HTMLFormElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            message: '',
            attachments: [],
        },
    });
    const message = form.watch('message');
    const attachments = form.watch('attachments');
    const isSendDisabled = useMemo(() => {
        return (
            isDisabled ||
            isSubmitting ||
            (!message.trim() && !attachments?.length)
        );
    }, [isDisabled, isSubmitting, message, attachments]);

    function onFilesAdded(files: FileList | null) {
        if (!files?.length) {
            return;
        }
        // TODO: tracker
        const newAttachments = [...form.getValues('attachments')];
        const errors = new Set<string>();
        for (const file of files) {
            const isDuplicate = newAttachments.some(
                (f) => f.name === file.name && f.size === file.size
            );
            if (isDuplicate) {
                continue;
            }
            const isFileCountValid = validateChatUploadFileCount(
                newAttachments.length + 1
            );
            if (typeof isFileCountValid === 'object') {
                errors.add(isFileCountValid.message);
                break;
            }
            const validationResult = validateChatUploadFile(file);
            if (validationResult.valid) {
                newAttachments.push(file);
            } else {
                errors.add(validationResult.message);
            }
        }
        form.setValue('attachments', newAttachments);
        for (const error of errors) {
            toast.error(error, {
                cancel: {
                    label: <XIcon className="size-4" />,
                    onClick: () => {},
                },
                cancelButtonStyle: {
                    backgroundColor: 'transparent',
                    minWidth: 0,
                },
            });
        }
        textareaRef.current?.focus();
    }

    function onAttachmentRemove(index: number) {
        form.setValue(
            'attachments',
            attachments.filter((_, i) => i !== index)
        );
    }

    function onSubmit(values: z.infer<typeof formSchema>) {
        if (isSendDisabled) {
            return;
        }
        onSend?.({ message: values.message, attachments: values.attachments });
        form.reset();
        textareaRef.current?.focus();
    }

    useEffect(() => {
        // Focus the input when the chat box is opened.
        if (isOpen) {
            textareaRef.current?.focus();
        }
    }, [isOpen]);

    return (
        <Form {...form}>
            <form
                ref={formRef}
                id={`chat-box-input-${formId}`}
                className={cn('flex flex-col gap-[8px]', className)}
                onSubmit={form.handleSubmit(onSubmit)}
            >
                <FormField
                    name="message"
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className="space-y-[8px]">
                            <FormControl ref={textareaRef}>
                                <Textarea
                                    // Need ! to override md:text-base class
                                    className="resize-none rounded-[8px] px-[12px] py-[8px] text-[16px] md:!text-[14px]"
                                    color="contrast"
                                    {...field}
                                    placeholder="Write a message..."
                                    rows={4}
                                    autoFocus
                                    onKeyDown={(e) => {
                                        // Use Ctrl + Enter to send the message.
                                        if (e.key === 'Enter' && e.ctrlKey) {
                                            e.preventDefault();
                                            formRef.current?.requestSubmit();
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage
                                className="text-[12px]"
                                variant="darker"
                            />
                        </FormItem>
                    )}
                />
                {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-[8px] text-[12px] leading-tight">
                        {attachments.map((attachment, index) => (
                            <div
                                key={`${attachment.name}-${attachment.size}`}
                                className="flex max-w-full items-center gap-[4px] rounded-md bg-gray-100 p-[8px] hover:bg-gray-200"
                            >
                                <span className="truncate">
                                    {attachment.name}
                                </span>
                                <button
                                    className="shrink-0"
                                    type="button"
                                    aria-label="Remove attachment"
                                    onClick={() => onAttachmentRemove(index)}
                                >
                                    <XIcon className="size-[12px]" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex items-center justify-between gap-[8px]">
                    <Button
                        className="gap-[4px] px-[12px] py-[8px] text-[14px] leading-tight text-gray-700 hover:bg-white/20"
                        type="button"
                        color="transparent"
                        size="narrow"
                        weight="normal"
                        textSize="small"
                        disabled={isSubmitting}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <PaperclipIcon className="size-[16px]" />
                        Upload a file
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        multiple
                        onChange={(e) => onFilesAdded(e.target.files)}
                    />
                    <Button
                        className="min-w-[120px] bg-brick-700 px-[24px] py-[8px] text-[14px] leading-tight text-white hover:bg-brick-900 hover:text-white"
                        loadingIconClassName="size-[20px]"
                        type="submit"
                        color="primary"
                        weight="normal"
                        textSize="small"
                        disabled={isSendDisabled}
                        isLoading={isSubmitting}
                    >
                        Send
                        <SendHorizontalIcon className="ml-[6px] size-[16px]" />
                    </Button>
                </div>
            </form>
        </Form>
    );
}
